import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { HolidaysService } from '../../../shared/services/holidays/holidays.service';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { RolesService } from '../../../shared/services/roles/roles.service';

@Component({
  selector: 'app-rule-group-form',
  imports: [
    CommonModule,
    MatIconModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    FormsModule,
    RouterLink,
    LoaderComponent,
  ],
  templateUrl: './rule-group-form.component.html',
  styleUrls: ['./rule-group-form.component.css', '../../../shared/styles/layout.css'],
})
export class RuleGroupFormComponent {
  // Mode management
  isEditMode: boolean = false;
  ruleGroupId: number | null = null;

  // Common properties
  myroles: any = {};
  ruleGroupForm: FormGroup;
  ruleGroup: any = {};
  isSubmitting = false;
  attemptedSubmit = false;
  returnLink: string = '/home/vacations/protocols';

  private _snackBar = inject(MatSnackBar);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private holidaysService: HolidaysService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private loaderService: LoaderService,
    private storageService: StorageService,
    private rolesService: RolesService
  ) {
    this.ruleGroupForm = this.fb.group({
      name: ['', [Validators.required]],
      // Add more form fields as needed
    });
  }

  get isLoading() {
    return this.loaderService.getLoader();
  }

  ngOnInit() {
    this.route.params.subscribe((params) => {
      if (params['id']) {
        this.isEditMode = true;
        this.ruleGroupId = +params['id'];
        this.loaderService.showLoader();
        this.loadRuleGroupData();
      } else {
        this.isEditMode = false;
        this.ruleGroupId = null;
      }
    });
    this.loadRoles();
  }

  loadRoles() {
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'Vacaciones',
      this.storageService
    );

    if (permissions) {
      this.myroles = permissions;

      const permissionMessage = this.isEditMode
        ? 'No tienes permisos para editar una regla'
        : 'No tienes permisos para añadir una regla';

      this.rolesService.checkWritePermission(
        'Vacaciones',
        this.storageService,
        this._snackBar,
        this.router,
        this.returnLink,
        permissionMessage
      );
    }
  }

  private loadRuleGroupData(): void {
    if (!this.ruleGroupId) return;

    this.holidaysService
      .getRuleGroupById(this.ruleGroupId)
      .pipe(
        finalize(() => {
          this.loaderService.hideLoader();
        })
      )
      .subscribe({
        next: (data) => {
          this.ruleGroup = data;
          this.ruleGroupForm.patchValue({
            name: data.name,
          });
        },
        error: (error) => {
          console.error('Error loading rule group:', error);
          this.snackBar.open(
            'Error al cargar los datos del grupo de reglas',
            'Cerrar',
            { duration: 3000 }
          );
          this.router.navigate([this.returnLink], { queryParams: { tab: 'grupos' } });
        },
      });
  }

  saveChanges(): void {
    this.loaderService.showLoader();
    if (this.isSubmitting) return;
    this.attemptedSubmit = true;

    if (!this.ruleGroupForm.valid) {
      this.loaderService.hideLoader();
      // Mark all controls as touched to trigger validation visuals
      Object.keys(this.ruleGroupForm.controls).forEach((key) => {
        const control = this.ruleGroupForm.get(key);
        control?.markAsTouched();
      });

      this.snackBar.open(
        'Por favor completa todos los campos requeridos',
        'Cerrar',
        { duration: 5000 }
      );
      return;
    }

    this.isSubmitting = true;

    if (this.isEditMode) {
      this.updateRuleGroup();
    } else {
      this.createRuleGroup();
    }
  }

  private createRuleGroup(): void {
    const formValue = this.ruleGroupForm.value;

    const body = {
      name: formValue.name,
      // Add more fields as needed
    };

    // TODO: Implement postRuleGroup in HolidaysService
    this.loaderService.hideLoader();
    this.isSubmitting = false;
    this.snackBar.open(
      'Funcionalidad de creación pendiente de implementación',
      'Cerrar',
      { duration: 3000 }
    );
    console.log('Rule group to create:', body);
  }

  private updateRuleGroup(): void {
    if (!this.ruleGroupId) return;

    const formValue = this.ruleGroupForm.value;

    const body = {
      name: formValue.name,
      // Add more fields as needed
    };

    this.holidaysService
      .updateRuleGroup(this.ruleGroupId, body)
      .pipe(
        finalize(() => {
          this.loaderService.hideLoader();
          this.isSubmitting = false;
        })
      )
      .subscribe({
        next: (response) => {
          this.snackBar.open(
            'Grupo de reglas actualizado correctamente',
            'Cerrar',
            {
              duration: 3000,
            }
          );
          this.router.navigate([this.returnLink], { queryParams: { tab: 'grupos' } });
        },
        error: (error) => {
          console.error('Error updating rule group:', error);
          this.snackBar.open(
            'Error al actualizar el grupo de reglas',
            'Cerrar',
            {
              duration: 3000,
            }
          );
        },
      });
  }

  cancel(): void {
    this.router.navigate([this.returnLink], { queryParams: { tab: 'grupos' } });
  }
}
